import React from "react";
import { Platform, StyleProp, StyleSheet, Text, TextProps, View, ViewProps, ViewStyle } from "react-native";
import { useEventListener } from "usehooks-ts";

import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";

import { PdClose } from "../Icons";
import { mergeRefs } from "../utils";
import { PdIconButton, tPdIconButtonProps } from "../forms/icon-button";
import { tSvgProps } from "../types";

type tModal = ViewProps & {
  containerStyle?: StyleProp<ViewStyle>;
  onClose?: () => void;
  closeOnOverlayClick?: boolean;
};

export const PdModal = React.forwardRef<View, React.PropsWithChildren<tModal>>((props, ref) => {
  const { style, containerStyle, onClose, closeOnOverlayClick = true, ...rest } = props;
  const kModalRef = React.useRef<View>(null);

  useEventListener("click", (event) => {
    if (Platform.OS === "web") {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const kPointerEvent = event as PointerEvent;
      kModalRef.current?.measure((_x, _y, width, height, pageX, pageY) => {
        const isInXRegion = kPointerEvent.pageX > pageX && kPointerEvent.pageX < pageX + width;
        const isInYRegion = kPointerEvent.pageY > pageY && kPointerEvent.pageY < pageY + height;

        if (!(isInXRegion && isInYRegion) && closeOnOverlayClick) {
          onClose?.();
        }
      });
    }
  });

  return (
    <View style={[styles.modalOverlay, containerStyle]}>
      <View ref={mergeRefs([kModalRef, ref])} style={[styles.modal, style]} {...rest} />
    </View>
  );
});

export const PdModalTitle: React.FC<TextProps> = ({ style, ...rest }) => (
  <Text style={[styles.modalTitle, style]} {...rest} />
);

export const PdModalDescription: React.FC<TextProps> = ({ style, ...rest }) => (
  <Text style={styles.modalDescription} {...rest} />
);

export const PdModalCloseButton = React.forwardRef<
  View,
  React.PropsWithChildren<Omit<tPdIconButtonProps, "icon">> & { iconStyle?: tSvgProps["style"] }
>((props, ref) => {
  const { style, iconStyle, ...rest } = props;
  return (
    <PdIconButton
      variant="ghost"
      ref={ref}
      icon={<PdClose style={[styles.modalCloseButtonIcon, iconStyle]} />}
      style={[styles.modalCloseButton, style]}
      {...rest}
    />
  );
});

const styles = StyleSheet.create({
  modal: {
    backgroundColor: kColors.white,
    flexShrink: 1,
  },
  modalCloseButton: {
    margin: 4 * kStyleConst.kSpacing,
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: 10,
  },
  modalCloseButtonIcon: {
    height: 15,
    width: 15,
  },
  modalDescription: {
    ...kFonts.poppinsRegularLight,
    letterSpacing: 1,
    marginBottom: 10 * kStyleConst.kSpacing,
  },
  modalOverlay: {
    alignItems: "center",
    backgroundColor: kColors.transparentBlack,
    bottom: 0,
    justifyContent: "center",
    left: 0,
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: 150,
  },
  modalTitle: {
    ...kFonts.poppinsExtraLargeMedium,
    color: kColors.brandDark,
    letterSpacing: 1.4,
    paddingRight: 4 * kStyleConst.kSpacing,
  },
});
